// ABOUTME: Material UI dashboard card component showing daily triangle tracking with developmental comparisons
// ABOUTME: Uses Recharts for age range visualization and displays today vs historical average data

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { getMobileChartMargins, MOBILE_BREAKPOINT_QUERY } from '../../utils/mobileOptimization';
import { filterDevelopmentalRanges, getAgeLabel, getAgeRangeText, getFirstName } from '../../utils/developmentalRangeUtils';

const TriangleTrackerCard = ({ data, error = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const chartMargins = getMobileChartMargins('standard', isMobile);

  // Loading state
  if (!data && !error) {
    return (
      <Card role="region" aria-label="Triangle tracker">
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card role="region" aria-label="Triangle tracker">
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const {
    today,
    historicalAverage,
    developmentalRanges,
    childName,
    childAge
  } = data;

  // Filter developmental ranges to always show exactly 3 ranges
  const { filteredRanges, showDataInColumn } = filterDevelopmentalRanges(
    developmentalRanges,
    childAge,
    childName,
    'minPerDay'
  );

  // Prepare chart data from filtered developmental ranges
  const chartData = filteredRanges.map((range, index) => {
    const isCustomChildRange = range.isCustomChildRange === true;
    const shouldShowData = index === showDataInColumn;

    return {
      ageRange: getAgeLabel(range),
      developmentalMin: isCustomChildRange ? null : range.minPerDay,
      developmentalMax: isCustomChildRange ? null : range.maxPerDay,
      todayCount: shouldShowData ? today.triangleCount : null,
      isCurrentAge: range.isCurrentAge,
      isCustomChildRange: isCustomChildRange,
      developmentalFill: range.isCurrentAge ? theme.palette.warning.light : theme.palette.grey[300],
      currentFill: theme.palette.warning.main
    };
  });

  const maxChartValue = Math.max(
    ...filteredRanges.map(r => r.maxPerDay),
    historicalAverage,
    today.triangleCount
  ) + 2;

  // Format historical average to 1 decimal place
  const formattedAverage = parseFloat(historicalAverage.toFixed(1));

  // Get first name and age range text
  const firstName = getFirstName(childName);
  const currentAgeRange = filteredRanges.find(range => range.isCurrentAge);
  const ageRangeText = getAgeRangeText(currentAgeRange, 'minPerDay', 'per day', 1);


  return (
    <Card role="region" aria-label="Triangle tracker">
      <CardContent>
        {/* Card Title */}
        <Typography variant="h5" component="h2" gutterBottom textAlign="center" sx={{ fontWeight: 'bold', mb: 3 }}>
          Daily Struggle (Triangles) vs Developmental Averages
        </Typography>

        {/* 50/50 Split Layout */}
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} sx={{ mb: 3 }}>
          {/* Left Side - Today's Count */}
          <Box flex={1} textAlign="center" sx={{ pr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Today:
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Typography
                variant="h2"
                component="div"
                sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', sm: '3rem' }, color: theme.palette.warning.main }}
              >
                {today.triangleCount}
              </Typography>
              <Typography
                variant="h5"
                color="textSecondary"
                sx={{ alignSelf: 'center', textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {today.triangleCount === 1 ? 'Triangle' : 'Triangles'}
              </Typography>
            </Box>
          </Box>

          {/* Right Side - Chips */}
          <Box flex={1} display="flex" flexDirection="column" justifyContent="center" gap={2} sx={{ pl: { xs: 0, sm: 2 } }}>
            {/* Position 1: Age Average Chip (NEW) */}
            <Chip
              data-testid="chip-age-average"
              label={`Avg. for Age: ${ageRangeText}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.grey[300],
                color: theme.palette.grey[800],
                border: `1px solid ${theme.palette.grey[600]}`
              }}
            />

            {/* Position 2: 12-Week Average Chip (UPDATED) */}
            <Chip
              data-testid="chip-12-week-average"
              label={`${firstName} 12 Week Avg: ${formattedAverage}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText
              }}
            />

            {/* Position 3: Today Count Chip (UPDATED) */}
            <Chip
              data-testid="chip-today-count"
              label={`${firstName} Today: ${today.triangleCount}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.warning.light,
                color: theme.palette.warning.contrastText
              }}
            />
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ width: '100%', height: { xs: 200, sm: 250 }, mb: 1 }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={chartMargins}
              barCategoryGap="20%"
            >
              <XAxis
                dataKey="ageRange"
                tick={{ fontSize: 12, textAnchor: 'middle' }}
                axisLine={{ stroke: theme.palette.grey[800], strokeWidth: 2 }}
                interval={0}
              />
              <YAxis
                domain={[0, maxChartValue]}
                tick={{ fontSize: 12 }}
                tickCount={maxChartValue + 1}
                interval={0}
                axisLine={{ stroke: theme.palette.grey[800], strokeWidth: 2 }}
              />

              {/* Developmental range backgrounds */}
              {chartData.map((entry, index) => {
                // Only show ReferenceArea if there's a goal (skip child's age column if outside ranges)
                if (entry.developmentalMin === null || entry.developmentalMax === null) {
                  return null;
                }

                return (
                  <ReferenceArea
                    key={`range-${index}`}
                    x1={entry.ageRange}
                    x2={entry.ageRange}
                    y1={entry.developmentalMin}
                    y2={entry.developmentalMax}
                    fill={entry.developmentalFill}
                    fillOpacity={0.6}
                    stroke={theme.palette.grey[600]}
                    strokeWidth={1}
                  />
                );
              })}

              {/* Today's count bar - Issue #963 */}
              <Bar dataKey="todayCount" radius={[4, 4, 0, 0]} maxBarSize={30}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`today-${index}`}
                    fill={entry.isCurrentAge ? theme.palette.warning.main : 'transparent'}
                    data-testid={entry.isCurrentAge ? "today-count-bar" : undefined}
                    data-value={entry.isCurrentAge ? today.triangleCount : undefined}
                  />
                ))}
              </Bar>

              {/* 12-week average line - Issue #963: Daily progress blue */}
              <ReferenceLine
                y={historicalAverage}
                stroke={theme.palette.primary.main}
                strokeWidth={3}
                strokeDasharray="5 5"
                data-testid="12-week-average-line"
                data-value={historicalAverage.toString()}
              />

            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Triangle Explanation */}
        <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2, fontStyle: 'italic' }}>
          Struggles are tracked as triangle events in your daily behavior log
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TriangleTrackerCard;